import { useState, useEffect, useCallback } from "react";
import { BellRing } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * 🔔 Personal Telegram alerts — link YOUR OWN Telegram DM so the bots ping
 * you: 🏦 bank alerts, 🌙 nightly "while you slept" digest, 📒 Sunday report
 * card, 🛑 budget-trip alerts. This is what brings users back every day.
 */
export const TelegramLinkCard = ({ accountId }) => {
  const [linked, setLinked] = useState(null);
  const [codeInfo, setCodeInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  const check = useCallback(async () => {
    if (!accountId) return;
    try {
      const { data } = await api.get(`/tg/link-status/${encodeURIComponent(accountId)}`);
      setLinked(Boolean(data.linked));
      if (data.linked) setCodeInfo(null);
    } catch { /* keep old */ }
  }, [accountId]);

  useEffect(() => { check(); }, [check]);
  useEffect(() => {
    if (!codeInfo || linked) return undefined;
    const t = setInterval(check, 5000); // fast poll while waiting for the link
    return () => clearInterval(t);
  }, [codeInfo, linked, check]);

  const getCode = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tg/link-code/${encodeURIComponent(accountId)}`);
      setCodeInfo(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Telegram bot not configured");
    } finally { setBusy(false); }
  };
  const unlink = async () => {
    setBusy(true);
    try {
      await api.post(`/tg/unlink/${encodeURIComponent(accountId)}`);
      setLinked(false);
      setCodeInfo(null);
      toast("🔕 Personal alerts off");
    } catch { toast.error("Unlink failed"); } finally { setBusy(false); }
  };

  if (!accountId) return null;
  return (
    <div data-testid="tg-link-card" className="rounded-lg border border-vts-line bg-vts-card p-3 mb-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <BellRing className="h-4 w-4 text-amber-300" />
        <p className="text-[12px] font-semibold text-white">Personal alerts — your bots ping YOUR Telegram</p>
        {linked ? (
          <span data-testid="tg-link-status" className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-vts-profit/15 border border-vts-profit/40 text-vts-profit">✅ LINKED</span>
        ) : (
          <span data-testid="tg-link-status" className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-vts-surface border border-vts-line text-vts-silver/50">off</span>
        )}
      </div>
      <p className="text-[10px] text-vts-silver/55 font-mono">🏦 "your bot banked +$2.10 while you slept" · 🌙 nightly digest · 📒 Sunday report card · 🛑 budget alerts — straight to your DM, even with the app closed.</p>
      {linked ? (
        <button data-testid="tg-unlink-btn" onClick={unlink} disabled={busy}
          className="h-8 px-3 rounded-md border border-vts-line bg-vts-surface text-vts-silver/60 text-[10px] font-mono hover:text-white transition disabled:opacity-50">
          🔕 Unlink
        </button>
      ) : codeInfo ? (
        <div className="space-y-1.5">
          <p className="text-[11px] font-mono text-white">
            1. Open <span className="text-vts-blue">@{codeInfo.bot_username}</span> → 2. tap START → it links automatically
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <a data-testid="tg-link-open" href={codeInfo.deep_link} target="_blank" rel="noopener noreferrer"
              className="h-9 px-4 inline-flex items-center rounded-md bg-vts-blue text-white text-[11px] font-bold font-mono uppercase tracking-wider hover:brightness-110 transition">
              Open Telegram →
            </a>
            <span className="text-[10px] font-mono text-vts-silver/55">or send this code to the bot:</span>
            <code data-testid="tg-link-code" className="px-2 py-1 rounded bg-vts-surface border border-amber-400/40 text-amber-300 font-mono text-[12px] font-bold tracking-widest">{codeInfo.code}</code>
          </div>
          <p className="text-[9px] font-mono text-vts-silver/40 animate-pulse">⏳ waiting for the link — this card updates itself…</p>
        </div>
      ) : (
        <button data-testid="tg-link-btn" onClick={getCode} disabled={busy}
          className="h-9 px-4 rounded-md bg-amber-400/90 hover:bg-amber-400 text-black text-[11px] font-bold font-mono uppercase tracking-wider transition disabled:opacity-50">
          🔔 Link Telegram
        </button>
      )}
    </div>
  );
};

export default TelegramLinkCard;
